'use client';

import { useState } from 'react';
import { ConfigurationPanel } from './configuration-panel';
import { QueryPanel } from './query-panel';
import { useAgentRetrievalTesting } from '@/modules/gpt-chats/hooks/use-agents';
import { useProjectStore } from '@/modules/identifier/state/use-project-store';
import { showErrorToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { useTranslation } from 'react-i18next';
import {
  IRetrievalQueryResult,
  IRetrievalAnalysisData,
} from '@/modules/gpt-chats/types/agent.service.type';

type AgentsRetrievalTestingProps = {
  agentId: string;
};

export const AgentsRetrievalTesting = ({ agentId }: AgentsRetrievalTestingProps) => {
  const { t } = useTranslation();
  const [enableEnhancement, setEnableEnhancement] = useState(true);
  const [relevanceScore, setRelevanceScore] = useState(0.7);
  const [recallNumber, setRecallNumber] = useState(10);
  const [searchType, setSearchType] = useState<'mixed' | 'semantic'>('mixed');
  const [semanticWeight, setSemanticWeight] = useState(90);
  const [denseWeight, setDenseWeight] = useState(50);
  const [keywordWeight, setKeywordWeight] = useState(50);
  const [rerankModel, setRerankModel] = useState(true);
  const [vectorQuery, setVectorQuery] = useState('');
  const [groundTruthTexts, setGroundTruthTexts] = useState<string[]>([]);
  const [hasResults, setHasResults] = useState(false);
  const [activeTab, setActiveTab] = useState('initial');
  const [results, setResults] = useState<IRetrievalQueryResult[]>([]);
  const [analysisData, setAnalysisData] = useState<IRetrievalAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const tenantId = useProjectStore().selectedProject?.tenantId || '';
  const { mutateAsync: testRetrieval } = useAgentRetrievalTesting(agentId);

  const hasEmptyGroundTruthError =
    groundTruthTexts.length > 0 && groundTruthTexts.every((text) => text.trim() === '');

  const handleRunQuery = async () => {
    if (!vectorQuery.trim()) {
      showErrorToast({ errors: t('PLEASE_ENTER_A_QUERY') });
      return;
    }

    if (!tenantId) {
      showErrorToast({ errors: t('PROJECT_KEY_NOT_FOUND') });
      return;
    }

    if (hasEmptyGroundTruthError) {
      showErrorToast({
        errors: t('GROUND_TRUTH_EMPTY_ERROR'),
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await testRetrieval({
        project_key: tenantId,
        enable_query_enhancement: enableEnhancement,
        enable_rerank: rerankModel,
        top_k: recallNumber,
        score_threshold: relevanceScore,
        query: vectorQuery,
        ground_truth_texts: groundTruthTexts,
        dense_weight: denseWeight,
        keyword_weight: keywordWeight,
      });

      if (response.is_success) {
        const processedResults = response.result.map((result, idx) => {
          if (result.query_type === 'sub-query') {
            const subQueryCount =
              response.result.slice(0, idx).filter((r) => r.query_type === 'sub-query').length + 1;
            return {
              ...result,
              query_type: `sub-query-${subQueryCount}`,
            };
          }
          return result;
        });

        setResults(processedResults);
        setAnalysisData(response.analysis_data);
        setHasResults(true);
      } else {
        showErrorToast({ errors: t('RETRIEVAL_TEST_FAILED') });
      }
    } catch (error) {
      showErrorToast({ errors: ErrorTransformer(error) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-auto flex-col gap-6 lg:h-[calc(100vh-220px)] lg:flex-row">
      <ConfigurationPanel
        enableEnhancement={enableEnhancement}
        setEnableEnhancement={setEnableEnhancement}
        relevanceScore={relevanceScore}
        setRelevanceScore={setRelevanceScore}
        recallNumber={recallNumber}
        setRecallNumber={setRecallNumber}
        searchType={searchType}
        setSearchType={setSearchType}
        semanticWeight={semanticWeight}
        setSemanticWeight={setSemanticWeight}
        denseWeight={denseWeight}
        setDenseWeight={setDenseWeight}
        keywordWeight={keywordWeight}
        setKeywordWeight={setKeywordWeight}
        rerankModel={rerankModel}
        setRerankModel={setRerankModel}
        groundTruthTexts={groundTruthTexts}
        setGroundTruthTexts={setGroundTruthTexts}
      />
      <QueryPanel
        vectorQuery={vectorQuery}
        setVectorQuery={setVectorQuery}
        hasResults={hasResults}
        handleRunQuery={handleRunQuery}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isLoading={isLoading}
        results={results}
        analysisData={analysisData}
        groundTruthTexts={groundTruthTexts}
        hasEmptyGroundTruthError={hasEmptyGroundTruthError}
      />
    </div>
  );
};
